import { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Bell, Save, Loader2, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import { partnerApi, formatPartnerError, canPartnerAdmin } from "./partnerApi";
import "./PartnerPortal.css";

export default function PartnerNotifications() {
  const { me } = useOutletContext();
  const canWrite = canPartnerAdmin(me);
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [digestBusy, setDigestBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await partnerApi.get("/partner/portal/notifications/preferences");
      setPrefs(data);
    } catch (e) { toast.error(formatPartnerError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await partnerApi.patch("/partner/portal/notifications/preferences", {
        email: prefs.email, in_portal: prefs.in_portal, events: prefs.events,
      });
      setPrefs(data);
      toast.success("Preferences saved");
    } catch (e) { toast.error(formatPartnerError(e)); }
    finally { setBusy(false); }
  };

  const generateDigest = async () => {
    setDigestBusy(true);
    try {
      const { data } = await partnerApi.post("/partner/portal/digest/generate");
      if (data.skipped) {
        toast.info(`Skipped: ${data.reason}`);
      } else {
        toast.success(`Digest sent to ${data.delivered_to?.length || 0} recipient(s)`);
      }
    } catch (e) { toast.error(formatPartnerError(e)); }
    finally { setDigestBusy(false); }
  };

  const previewPdf = () => {
    const token = localStorage.getItem("hw_partner_access_token");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/partner/portal/digest/preview.pdf`,
           { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
        // Revoke after 60s to release memory once the tab has loaded it.
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      })
      .catch((e) => toast.error(`Preview failed: ${e.message || e}`));
  };

  if (loading || !prefs) {
    return (
      <div style={{ color: "#5EEAD4" }}>
        <Loader2 className="w-4 h-4 animate-spin" style={{ display: "inline" }} /> loading…
      </div>
    );
  }

  return (
    <div data-testid="pp-notifications">
      <h1 className="hw-pp__page-title">
        <Bell className="w-5 h-5"
                style={{ display: "inline", verticalAlign: "text-bottom", marginRight: "0.4rem" }} />
        Notification Preferences
      </h1>
      <p className="hw-pp__page-sub">
        Control which HarborWell events reach your team. Preferences apply at the org level.
        {!canWrite && " Editing requires OWNER or ADMIN."}
      </p>

      <div className="hw-pp__panel">
        <h3>Delivery channels</h3>
        <label style={{ display: "flex", alignItems: "center", gap: "0.5rem",
                          marginBottom: "0.4rem", cursor: canWrite ? "pointer" : "default" }}>
          <input type="checkbox" checked={prefs.email} disabled={!canWrite}
                  onChange={(e) => setPrefs({...prefs, email: e.target.checked})}
                  data-testid="pp-notif-email" />
          <Mail className="w-3.5 h-3.5" /> Email delivery (to org Owners &amp; Admins)
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: canWrite ? "pointer" : "default" }}>
          <input type="checkbox" checked={prefs.in_portal} disabled={!canWrite}
                  onChange={(e) => setPrefs({...prefs, in_portal: e.target.checked})}
                  data-testid="pp-notif-in-portal" />
          <Bell className="w-3.5 h-3.5" /> In-portal alerts (Overview banner)
        </label>
      </div>

      <div className="hw-pp__panel">
        <h3>Events</h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.35rem" }}>
          {(prefs.catalog || []).map((ev) => (
            <label key={ev.key}
                    style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem",
                                padding: "0.45rem 0.55rem",
                                background: "rgba(15,23,42,0.5)",
                                border: "1px solid rgba(94,234,212,0.1)",
                                borderRadius: "0.4rem", cursor: canWrite ? "pointer" : "default" }}>
              <input type="checkbox"
                      checked={!!(prefs.events || {})[ev.key]}
                      disabled={!canWrite}
                      onChange={(e) => setPrefs({
                        ...prefs,
                        events: { ...(prefs.events || {}), [ev.key]: e.target.checked },
                      })}
                      data-testid={`pp-notif-ev-${ev.key}`} />
              <div>
                <div style={{ color: "#F1F5F9", fontSize: "0.83rem", fontWeight: 600 }}>
                  {ev.label}
                </div>
                <div style={{ color: "#64748B", fontSize: "0.7rem" }}>
                  <code>{ev.key}</code>
                </div>
              </div>
            </label>
          ))}
        </div>
        {canWrite && (
          <button className="hw-pp__btn hw-pp__btn--primary" onClick={save}
                    disabled={busy} style={{ marginTop: "0.7rem" }}
                    data-testid="pp-notif-save">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save preferences
          </button>
        )}
      </div>

      <div className="hw-pp__panel">
        <h3>
          <span>Weekly integration digest</span>
          <small>Monday 07:15 America/New_York</small>
        </h3>
        <div style={{ color: "#CBD5E1", fontSize: "0.85rem", marginBottom: "0.5rem" }}>
          Owners &amp; Admins receive a branded PDF summarising the past 7 days of
          API usage, webhook health, and integration status. Skipped automatically
          if email is disabled above.
        </div>
        <div style={{ display: "flex", gap: "0.4rem" }}>
          <button className="hw-pp__btn" onClick={previewPdf}
                   data-testid="pp-notif-preview-pdf">
            Preview PDF
          </button>
          {canWrite && (
            <button className="hw-pp__btn hw-pp__btn--primary"
                     onClick={generateDigest} disabled={digestBusy}
                     data-testid="pp-notif-send-digest">
              {digestBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Send digest now
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
